import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SiteHeader } from "@/components/site/SiteHeader";
import { Input } from "@/components/ui/input";
import { STAFF_ROLES, useAuth } from "@/context/AuthContext";
import { LOGIN, REGISTER } from "@/constants/testIds";
import { BRAND_PHOTOS } from "@/lib/constants";
import { errMsg } from "@/lib/api";

export default function AuthPage({ mode = "login" }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && f.password !== f.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const u = isLogin ? await login(f.email, f.password)
        : await register({ name: f.name, email: f.email, phone: f.phone || null, password: f.password });
      const staff = (u.roles || []).some((r) => STAFF_ROLES.includes(r));
      const next = sp.get("next") || "";
      const ok = next.startsWith("/") && !next.startsWith("//") && !(staff && next.startsWith("/portal")) && (staff || !next.startsWith("/admin"));
      nav(ok ? next : staff ? "/admin" : "/portal", { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Could not sign in"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F4F4F4]" data-testid={`${mode}-page`}>
      <div className="relative hidden lg:block bg-[#020165] am-grain overflow-hidden">
        <img src={BRAND_PHOTOS[0]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020165] to-[#020165]/40" />
        <div className="relative p-8 h-full flex flex-col"><SiteHeader />
          <div className="mt-auto text-white max-w-md pb-6">
            <h2 className="font-display text-4xl font-extrabold tracking-tight">Your car's whole story, in one place.</h2>
            <p className="mt-3 text-white/70">Quotes, bookings, live job progress, invoices and messages with your service advisor.</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="lg:hidden p-4 bg-[#020165]"><SiteHeader /></div>
        <form onSubmit={submit} className="m-auto w-full max-w-sm p-6 space-y-4">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0D141A]">{isLogin ? "Sign in" : "Create your account"}</h1>
          {!isLogin && <Input required placeholder="Full name" value={f.name} onChange={set("name")} data-testid={REGISTER.nameInput} />}
          <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid={isLogin ? LOGIN.emailInput : REGISTER.emailInput} />
          {!isLogin && <Input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} data-testid="register-phone-input" />}
          <Input required type="password" minLength={isLogin ? 1 : 8} placeholder="Password" value={f.password} onChange={set("password")} data-testid={isLogin ? LOGIN.passwordInput : REGISTER.passwordInput} />
          {!isLogin && <Input required type="password" placeholder="Confirm password" value={f.confirm} onChange={set("confirm")} data-testid={REGISTER.passwordConfirmInput} />}
          <button disabled={busy} data-testid={isLogin ? LOGIN.submitButton : REGISTER.submitButton}
            className="w-full rounded-full bg-[#020165] text-white font-semibold py-3 hover:bg-[#0a0a8a] disabled:opacity-60 transition-colors">
            {busy ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
          </button>
          <p className="text-sm text-[#56585E] text-center">
            {isLogin ? <>New to Accelary? <Link to="/register" className="font-semibold text-[#020165] underline" data-testid={LOGIN.registerLink}>Create an account</Link></>
              : <>Already registered? <Link to="/login" className="font-semibold text-[#020165] underline" data-testid={REGISTER.loginLink}>Sign in</Link></>}
          </p>
          <p className="text-xs text-[#56585E] text-center">Just want to check a job? <Link to="/track" className="underline">Track with your job reference</Link></p>
          {isLogin && <p className="pt-4 text-center"><Link to="/insurer/login" className="text-xs text-[#56585E] hover:text-[#020165] underline underline-offset-4" data-testid="login-insurer-link">Log in as an Insurer</Link></p>}
        </form>
      </div>
    </div>
  );
}
